import type {
  NotificationUsers,
  GenericError,
  NotificationSettings,
} from 'src/types/referral-v2';
import type {Dispatch, ThunkAction} from 'src/reducers';
import {thunkify as flow} from 'src/utils/thunks';
import * as reduxApi from 'src/utils/redux-api';
import {showApiError} from 'src/action-creators/modal';
import {DEFAULT_ERROR} from 'src/action-creators/referral-v2/referral-jobs';
import {toastMessage} from 'src/components/referral-v2/tracking/referral-confirm-modal';
import {toastApi} from '@spaced-out/ui-design-system/lib/components/index';


export const NOTIFICATION_USERS_LOADING = 'referral/notification-users-loading';
export const NOTIFICATION_USERS = 'referral/notification-users';
export const NOTIFICATION_USERS_ERROR = 'referral/notification-users-error';
export const NOTIFICATION_CONFIG_USERS_LOADING =
  'referral/notification-config-users-loading';
export const NOTIFICATION_CONFIG_USERS = 'referral/notification-config-users';
export const NOTIFICATION_CONFIG_USERS_ERROR =
  'referral/notification-config-users-error';

type NotificationUsersLoadingAction = {
  type: typeof NOTIFICATION_USERS_LOADING;
};

type NotificationUsersAction = {
  type: typeof NOTIFICATION_USERS;
  payload: NotificationUsers[] | null | undefined;
};

type NotificationUsersErrorAction = {
  type: typeof NOTIFICATION_USERS_ERROR;
  payload: GenericError | null | undefined;
};
type NotificationConfigUsersLoadingAction = {
  type: typeof NOTIFICATION_CONFIG_USERS_LOADING;
};

type NotificationConfigUsersAction = {
  type: typeof NOTIFICATION_CONFIG_USERS;
  payload: NotificationUsers[] | null | undefined;
};

type NotificationConfigUsersErrorAction = {
  type: typeof NOTIFICATION_CONFIG_USERS_ERROR;
  payload: GenericError | null | undefined;
};

export type NotificationUsersActions =
  | NotificationUsersAction
  | NotificationUsersErrorAction
  | NotificationUsersLoadingAction
  | NotificationConfigUsersAction
  | NotificationConfigUsersErrorAction
  | NotificationConfigUsersLoadingAction;

const recieveNotificationUsers = (
  payload: NotificationUsers[],
): NotificationUsersAction => ({
  type: NOTIFICATION_USERS,
  payload,
});

const getNotificationUsersLoading = (): NotificationUsersLoadingAction => ({
  type: NOTIFICATION_USERS_LOADING,
});

const getNotificationUsersError = (
  payload?: GenericError | null,
): NotificationUsersErrorAction => ({
  type: NOTIFICATION_USERS_ERROR,
  payload,
});

const recieveNotificationConfigUsers = (
  payload: NotificationUsers[],
): NotificationConfigUsersAction => ({
  type: NOTIFICATION_CONFIG_USERS,
  payload,
});

const getNotificationConfigUsersLoading =
  (): NotificationConfigUsersLoadingAction => ({
    type: NOTIFICATION_CONFIG_USERS_LOADING,
  });

const getNotificationConfigUsersError = (
  payload?: GenericError | null,
): NotificationConfigUsersErrorAction => ({
  type: NOTIFICATION_CONFIG_USERS_ERROR,
  payload,
});

export const getNotificationUsers: (programId: string) => ThunkAction<void> =
  flow()((programId) => (dispatch: Dispatch) => {
    // @ts-expect-error - TS2769 - No overload matches this call.
    dispatch(getNotificationUsersLoading());

    return dispatch(
      reduxApi.get(`referral/program/${programId}/notification/all-users`),
    ).then(
      // @ts-expect-error - TS2345 - Argument of type '(response: NotificationUsers[]) => void' is not assignable to parameter of type '(value: unknown) => void | PromiseLike<void>'.
      (response: NotificationUsers[]) => {
        // @ts-expect-error - TS2769 - No overload matches this call.
        dispatch(recieveNotificationUsers(response));
      },
      (error) => {
        dispatch(
          // @ts-expect-error - TS2769 - No overload matches this call.
          getNotificationUsersError(error?.response?.errors || DEFAULT_ERROR),
        );
      },
    );
  });

export const getNotificationConfigUsers: (
  programId: string,
) => ThunkAction<void> = flow()((programId) => (dispatch: Dispatch) => {
  // @ts-expect-error - TS2769 - No overload matches this call.
  dispatch(getNotificationConfigUsersLoading());

  return dispatch(
    reduxApi.get(`referral/program/${programId}/notification/users`),
  ).then(
    // @ts-expect-error - TS2345 - Argument of type '(response: NotificationUsers[]) => void' is not assignable to parameter of type '(value: unknown) => void | PromiseLike<void>'.
    (response: NotificationUsers[]) => {
      // @ts-expect-error - TS2769 - No overload matches this call.
      dispatch(recieveNotificationConfigUsers(response));
    },
    (error) => {
      dispatch(
        // @ts-expect-error - TS2769 - No overload matches this call.
        getNotificationConfigUsersError(
          error?.response?.errors || DEFAULT_ERROR,
        ),
      );
    },
  );
});

export const addNotificationUsers: (
  programId: string,
  payload: {
    email: string[];
  },
) => ThunkAction<void> = flow()(
  (programId, payload) => (dispatch: Dispatch) => {
    return dispatch(
      reduxApi.post(
        `referral/program/${programId}/notification/add-user`,
        payload,
      ),
    )
      .then(() => {
        toastApi.show(toastMessage({message: 'User added successfully'}));
        dispatch(getNotificationUsers(programId));
        dispatch(getNotificationConfigUsers(programId));
      })
      .catch((error) => {
        if (error?.response?.status === 409) {
          toastApi.show(
            toastMessage({message: 'User Already Exist', semantic: 'danger'}),
          );
          return;
        }
        dispatch(showApiError(error, error.response.message));
      });
  },
);

export const updateUserNotificationSettings: (
  programId: string,
  payload: {
    email: string;
    settings: NotificationSettings;
  },
) => ThunkAction<void> = flow()(
  (programId, payload) => (dispatch: Dispatch) => {
    return dispatch(
      reduxApi.post(
        `referral/program/${programId}/notification/user-settings`,
        payload,
      ),
    ).then(
      () => {
        toastApi.show(
          toastMessage({message: 'User notification updated successfully'}),
        );
        dispatch(getNotificationConfigUsers(programId));
      },
      (error) => {
        dispatch(showApiError(error, error.response.message));
      },
    );
  },
);

export const deleteNotificationUser: (
  email: string,
  programId: string,
) => ThunkAction<void> = flow()((email, programId) => (dispatch: Dispatch) => {
  return dispatch(
    reduxApi.post(`referral/program/${programId}/notification/delete-user`, {
      email,
    }),
  ).then(() => {
    toastApi.show(toastMessage({message: 'User removed successfully'}));
    dispatch(getNotificationUsers(programId));
    dispatch(getNotificationConfigUsers(programId));
  });
});
